import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

const badgeVariants = cva(
  'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default:
          'border border-neutral-300 bg-neutral-100 text-neutral-800 hover:bg-neutral-200 hover:border-neutral-400',
        secondary:
          'border border-[var(--color-secondary-500)] bg-[var(--color-secondary-100)] text-[var(--color-secondary-700)] hover:bg-blue-200 hover:border-blue-600',
        destructive:
          'border border-[var(--color-error-500)] bg-[var(--color-error-500)] text-white hover:bg-red-600 hover:border-red-700',
        outline:
          'border border-neutral-300 text-neutral-700 hover:bg-neutral-100 hover:text-neutral-800 hover:border-neutral-400',
        success:
          'border border-success-500 bg-success-10 text-success-700 hover:bg-success-100 hover:border-success-600',
        warning:
          'border border-warning-500 bg-warning-10 text-warning-700 hover:bg-warning-100 hover:border-warning-600',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>, Omit<VariantProps<typeof badgeVariants>, 'variant'> {
  variant?: VariantProps<typeof badgeVariants>['variant'] | null;
}

function Badge({ className, variant, ...props }: BadgeProps) {
  const variantClasses =
    variant === null
      ? 'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2'
      : badgeVariants({ variant });
  return <div className={cn(variantClasses, className)} {...props} />;
}

export { Badge, badgeVariants };
